/**
 * The five states every data surface must implement (design.md §5).
 *
 * They live together on purpose: a screen that imports one of these and not the
 * others is visibly incomplete at review time.
 */

import { AlertCircle, Archive, FilterX, Inbox, Lock, RefreshCw, SearchX } from 'lucide-react'
import type * as React from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/surface'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

function Frame({
  icon,
  title,
  children,
  action,
  tone = 'muted',
}: {
  icon: React.ReactNode
  title: string
  children?: React.ReactNode
  action?: React.ReactNode
  tone?: 'muted' | 'danger'
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <div
        className={cn(
          'flex size-10 items-center justify-center rounded-lg [&_svg]:size-5',
          tone === 'danger' ? 'bg-danger-soft text-danger' : 'bg-surface-2 text-faint',
        )}
      >
        {icon}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-text">{title}</p>
        {children ? <p className="max-w-sm text-xs text-muted">{children}</p> : null}
      </div>
      {action}
    </div>
  )
}

/** 1. Loading — skeleton rows at the real row height, so nothing jumps. */
export function TableSkeleton({ rows = 8, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-border-soft" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="flex items-center gap-3 px-3"
          style={{ height: 'var(--row-h)' }}
        >
          {Array.from({ length: cols }).map((__, c) => (
            <Skeleton
              key={c}
              className="h-3"
              style={{ width: c === 0 ? '22%' : `${Math.max(10, 60 / cols)}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/** 2. Empty — says what lives here and offers the primary action. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: React.ReactNode
}) {
  return (
    <Frame icon={<Inbox />} title={title} action={action}>
      {description}
    </Frame>
  )
}

/** 3. Filtered empty — deliberately different from empty, with a way out. */
export function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Frame
      icon={<FilterX />}
      title="No matches"
      action={
        <Button size="sm" variant="secondary" onClick={onClear}>
          Clear filters
        </Button>
      }
    >
      Nothing matches the current filters. Try widening them.
    </Frame>
  )
}

/**
 * 4. Error — the API's own message, a retry, and the request id for support.
 * A 403 is routed to NoAccessState instead, because that is not an error the
 * user can act on by retrying.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const apiError = error instanceof ApiError ? error : null

  if (apiError?.isForbidden) {
    return <NoAccessState message={apiError.message} />
  }

  return (
    <Frame
      icon={<AlertCircle />}
      title="Something failed"
      tone="danger"
      action={
        onRetry ? (
          <Button size="sm" variant="secondary" onClick={onRetry}>
            <RefreshCw />
            Retry
          </Button>
        ) : undefined
      }
    >
      <>
        {apiError?.message ?? 'An unexpected error occurred.'}
        {apiError?.requestId ? (
          <span className="mt-2 block font-mono text-[11px] text-faint">
            request {apiError.requestId}
          </span>
        ) : null}
      </>
    </Frame>
  )
}

/**
 * 4b. Failed, inside a small surface (a related list, a tile, a preview): one line and
 * Retry, so the section stays where it was instead of vanishing. A refusal says so and
 * offers no Retry — retrying cannot change it.
 */
export function InlineError({
  error,
  onRetry,
  what = 'this',
  className,
}: {
  error: unknown
  onRetry: () => void
  /** What failed to load: "these contacts". */
  what?: string
  className?: string
}) {
  if (error instanceof ApiError && error.isForbidden) {
    return <p className={cn('text-xs text-muted', className)}>You don&apos;t have access.</p>
  }
  return (
    <p className={cn('flex flex-wrap items-center gap-x-2 text-xs text-danger', className)}>
      Could not load {what}.
      <Button size="sm" variant="ghost" onClick={onRetry}>
        <RefreshCw />
        Retry
      </Button>
    </p>
  )
}

/**
 * 5. No access — names the thing and who to ask. design.md §5: never a 404,
 * because pretending it does not exist is worse than saying no.
 */
export function NoAccessState({ thing, message }: { thing?: string; message?: string }) {
  return (
    <Frame icon={<Lock />} title="You don't have access">
      {message ??
        `You don't have permission to view ${thing ?? 'this'}. Ask an administrator if you need it.`}
    </Frame>
  )
}

/**
 * 6. Gone — a record that is not there (design.md §5).
 *
 * Archived and never-existed read differently on purpose: in a product where
 * nothing is deleted, telling someone an archived record "doesn't exist" would
 * be a lie. Neither offers Retry — retrying cannot change the answer.
 */
export function NotFoundState({
  thing,
  archived,
  message,
  action,
}: {
  thing: string
  archived?: boolean
  message?: string
  action?: React.ReactNode
}) {
  return (
    <Frame
      icon={archived ? <Archive /> : <SearchX />}
      title={archived ? `This ${thing} was archived` : `This ${thing} no longer exists`}
      action={action}
    >
      {message ??
        (archived
          ? 'It is hidden from lists and the API, but its data is still here and it can be restored.'
          : 'It may have been removed, or the link may be wrong.')}
    </Frame>
  )
}
